import React, { useState } from 'react';
import { Input } from '../ui/input';
import { CheckCircle2, XCircle, Sparkles, RotateCcw } from 'lucide-react';

const FillInBlank = ({ question, answer, onAnswer, showResult, wildcardResult, isEvaluatingWildcard, onRetry }) => {
  const [inputValue, setInputValue] = useState(answer || '');

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    onAnswer(value);
  };

  // Normal mode: check against missing_words (mirrors QuizLesson.checkAnswer logic)
  const isCorrectNormal = () => {
    if (!showResult || question.wildcard) return null;
    const normalized = inputValue.toLowerCase().trim();
    const wordsToCheck = question.missing_words?.length
      ? question.missing_words
      : question.correct_answer != null ? [String(question.correct_answer)] : [];
    return wordsToCheck.some(word => word.toLowerCase().trim() === normalized);
  };

  const correct = question.wildcard ? wildcardResult?.is_correct : isCorrectNormal();

  // Wildcard mode: textarea + AI evaluation
  if (question.wildcard) {
    return (
      <div className="space-y-4">
        <div className="bg-purple-50 p-6 rounded-xl">
          <p className="text-lg text-gray-900 whitespace-pre-wrap">{question.question}</p>
          <p className="text-sm text-purple-600 mt-2">✨ Resposta aberta — a Simby vai avaliar sua resposta</p>
        </div>

        <textarea
          value={inputValue}
          onChange={handleChange}
          disabled={showResult || isEvaluatingWildcard}
          placeholder="Digite sua resposta aqui..."
          rows={4}
          className={`w-full px-4 py-3 border-2 rounded-xl text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 ${
            showResult
              ? correct
                ? 'border-green-500 bg-green-50'
                : 'border-red-500 bg-red-50'
              : 'border-purple-300'
          }`}
        />

        {isEvaluatingWildcard && (
          <div className="flex items-center space-x-2 text-purple-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
            <span className="text-sm">Simby está avaliando sua resposta...</span>
          </div>
        )}

        {showResult && wildcardResult && (
          <div className={`p-4 rounded-xl border-2 ${correct ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
            <div className="flex items-center space-x-2 mb-2">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className={`font-semibold ${correct ? 'text-green-700' : 'text-red-700'}`}>
                {correct ? '✅ Boa resposta!' : '❌ Tente aprofundar um pouco mais'}
              </span>
            </div>
            <p className="text-gray-700">{wildcardResult.feedback}</p>
            {!correct && onRetry && (
              <button
                onClick={() => { setInputValue(''); onAnswer(''); onRetry(); }}
                className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Escrever novamente
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Normal mode
  return (
    <div className="space-y-4">
      <div className="bg-purple-50 p-6 rounded-xl">
        <p className="text-lg text-gray-900 whitespace-pre-wrap">{question.question}</p>
      </div>

      <div className="relative">
        <Input
          type="text"
          value={inputValue}
          onChange={handleChange}
          disabled={showResult}
          placeholder="Digite sua resposta..."
          className={`text-lg p-4 ${
            showResult
              ? correct
                ? 'border-2 border-green-500 bg-green-50'
                : 'border-2 border-red-500 bg-red-50'
              : 'border-2 border-purple-300 focus:border-purple-500'
          }`}
        />
        {showResult && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {correct ? (
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            ) : (
              <XCircle className="w-6 h-6 text-red-500" />
            )}
          </div>
        )}
      </div>

      {showResult && !correct && question.missing_words?.length > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Resposta esperada:</span>{' '}
            {question.missing_words.join(' / ')}
          </p>
        </div>
      )}
    </div>
  );
};

export default FillInBlank;
